import { clamp } from '../../model/utility';
import { DashboardWidgetInstance, GridRect, GridSize } from './dashboard-layout';
import { DashboardWidgetSize } from '../widget/dashboard-widget-definition';

export interface GeometryRules {
  columns: number;
  /** undefined for unknown types; they are only kept inside the columns */
  sizeLimits(type: string): DashboardWidgetSize | undefined;
  /** Hidden widgets take no space among the visible ones; they are stacked below them */
  isVisible(widget: Pick<DashboardWidgetInstance, 'type'>): boolean;
}

export interface GridPoint {
  x: number;
  y: number;
}

type Widgets = readonly DashboardWidgetInstance[];

/**
 * Clamps every widget to its size limits and the columns, resolves overlaps (earlier reading order wins),
 * compacts upward and stacks hidden widgets below the visible ones.
 */
export function normalizeGeometry(widgets: Widgets, rules: GeometryRules): DashboardWidgetInstance[] {
  const entries = widgets.map((widget) => ({
    widget,
    visible: rules.isVisible(widget),
    rect: clampRect(widget, rules.sizeLimits(widget.type), rules.columns),
  }));
  const visible = entries.filter((entry) => entry.visible).map(({ rect }) => rect);
  const hidden = entries.filter((entry) => !entry.visible).map(({ rect }) => rect);

  resolveOverlaps(visible);
  compactUp(visible, 0);
  compactUp(hidden, layoutRows(visible));

  return entries.map(({ widget, rect }) => withRect(widget, rect));
}

/** First spot among the visible widgets, scanning rows top to bottom and columns left to right; else below them. */
export function findFreeSpot(widgets: Widgets, size: GridSize, rules: GeometryRules): GridPoint {
  const visible = widgets.filter((widget) => rules.isVisible(widget));
  const cols = sanitizeColumns(rules.columns);
  const w = clamp(toCell(size.w, 1), 1, cols);
  const h = Math.max(1, toCell(size.h, 1));
  const floor = layoutRows(visible);

  for (let y = 0; y < floor; y++) {
    for (let x = 0; x <= cols - w; x++) {
      if (!collidesWithAny({ x, y, w, h }, visible)) {
        return { x, y };
      }
    }
  }

  return { x: 0, y: floor };
}

/** Sorted by row, then column; ties keep their input order. */
export function readingOrder<W extends GridRect>(widgets: readonly W[]): W[] {
  return [...widgets].sort((a, b) => a.y - b.y || a.x - b.x);
}

/** The lowest bottom edge */
export function layoutRows(widgets: readonly GridRect[]): number {
  return widgets.reduce((bottom, widget) => Math.max(bottom, widget.y + widget.h), 0);
}

function withRect(widget: DashboardWidgetInstance, { x, y, w, h }: GridRect): DashboardWidgetInstance {
  return widget.x === x && widget.y === y && widget.w === w && widget.h === h ? widget : { ...widget, x, y, w, h };
}

/** Clamps w/h to the limits and the columns, x to [0, columns - w] and y to >= 0; non-finite sizes use the default. */
function clampRect(rect: GridRect, limits: DashboardWidgetSize | undefined, columns: number): GridRect {
  const cols = sanitizeColumns(columns);
  const w = clampSpan(rect.w, limits?.default.w, limits?.min?.w, Math.min(limits?.max?.w ?? cols, cols));
  const h = clampSpan(rect.h, limits?.default.h, limits?.min?.h, limits?.max?.h ?? Number.POSITIVE_INFINITY);

  return { x: clamp(toCell(rect.x, 0), 0, cols - w), y: Math.max(0, toCell(rect.y, 0)), w, h };
}

/** Lifts every rect to at least `floor`, then slides each up as far as it can go, in reading order. */
function compactUp(rects: GridRect[], floor: number): void {
  const settled: GridRect[] = [];
  for (const rect of readingOrder(rects)) {
    rect.y = Math.max(rect.y, floor);
    while (rect.y > floor && !collidesWithAny({ ...rect, y: rect.y - 1 }, settled)) {
      rect.y--;
    }

    pushBelowCollisions(rect, settled);
    settled.push(rect);
  }
}

/** Earlier reading order wins: later rects are pushed below what they overlap */
function resolveOverlaps(rects: GridRect[]): void {
  const settled: GridRect[] = [];
  for (const rect of readingOrder(rects)) {
    pushBelowCollisions(rect, settled);
    settled.push(rect);
  }
}

function pushBelowCollisions(rect: GridRect, settled: readonly GridRect[]): void {
  let collider = settled.find((other) => other !== rect && collides(rect, other));
  while (collider) {
    rect.y = collider.y + collider.h;
    collider = settled.find((other) => other !== rect && collides(rect, other));
  }
}

function collidesWithAny(rect: GridRect, others: readonly GridRect[]): boolean {
  return others.some((other) => collides(rect, other));
}

function collides(a: GridRect, b: GridRect): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

function clampSpan(value: number, fallback: number | undefined, min: number | undefined, max: number): number {
  const upper = Math.max(1, max);
  const lower = Math.min(Math.max(1, min ?? 1), upper);

  return clamp(toCell(value, toCell(fallback ?? lower, lower)), lower, upper);
}

function sanitizeColumns(columns: number): number {
  return Math.max(1, toCell(columns, 1));
}

function toCell(value: number, fallback: number): number {
  return Number.isFinite(value) ? Math.round(value) : fallback;
}
